/* eslint-disable react/prop-types */
"use client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import { resolveClubImageSrc } from "../../utils/clubImages";
import { buildClubPath } from "../../utils/clubUrl";
import {
  DELETED_USER_LABEL,
  formatUserHandle,
  isDeletedAccountUser,
} from "../../utils/deletedAccount";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

// Format large numbers: K (thousands), M (millions), B (billions) with one decimal
function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n >= 1e9) return (n / 1e9).toFixed(1) + "\u00A0B";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "\u00A0M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "\u00A0K";
  return String(Math.round(n));
}

const normalizeRole = (r) => (r || "").toString().toLowerCase().trim();

const isHostRole = (r) => {
  const v = normalizeRole(r);
  return v === "host" || v === "admin" || v === "owner" || v === "creator";
};

const participantRole = (participant) =>
  participant?.chat_role ||
  participant?.role ||
  participant?.member_role ||
  participant?.event_role ||
  participant?.author_role ||
  null;

const participantPriority = (participant) => {
  if (isHostRole(participantRole(participant))) return 0;
  if (participant?.has_premium_subscription) return 1;
  return 2;
};

export const prioritizePremiumChatParticipants = (participants = []) =>
  participants
    .map((participant, index) => ({ participant, index }))
    .sort((left, right) => {
      const priorityDifference =
        participantPriority(left.participant) - participantPriority(right.participant);
      if (priorityDifference !== 0) return priorityDifference;

      return left.index - right.index;
    })
    .map(({ participant }) => participant);

function ParticipantClubCard({ club, onOpenClub }) {
  const { t } = useTranslation("menu");
  const heroSrc = resolveClubImageSrc(club);
  const clubPath = club?.public_path || buildClubPath(club?.id, club?.name);

  return (
    <button
      type="button"
      onClick={() => onOpenClub?.(club, clubPath)}
      className="relative w-full rounded-2xl border border-solid border-white/50 bg-transparent px-3 py-3 text-left active:scale-[0.995]"
      aria-label={t("participants.club_card.open_aria", { name: club?.name || "" })}
    >
      <div className="flex items-center gap-3">
        <div className="h-[58px] w-[58px] shrink-0 overflow-hidden rounded-full bg-white/10">
          {heroSrc ? (
            <img
              src={heroSrc}
              alt={club?.name || ""}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : null}
        </div>

        <div className="min-w-0 flex-1">
          <div className="truncate text-[18px] font-extrabold leading-5 text-white">
            {club?.name}
          </div>
          <div className="mt-1 truncate text-[13px] font-medium leading-[1.15rem] text-white/75">
            {club?.short_description || ""}
          </div>
        </div>
      </div>
    </button>
  );
}

function ParticipantCard({
  user,
  canKick = false,
  onKickClick,
  onOpenProfile,
  onOpenAvatarProfile,
  onSelectParticipant,
  unreadCount,
  isHost = false,
  badgeLabel = null,
  selected = false,
  selectionMode = false,
  isSelectable = true,
  avatarSrcOverride = null,
  surfaceControls = false,
  showKickControl = true,
  roleBorder = null,
}) {
  const { openProfileCard } = useProfileCardModal();
  const isDeletedUser = isDeletedAccountUser(user);
  const code = isDeletedUser ? "" : user?.code;
  const username = isDeletedUser
    ? DELETED_USER_LABEL
    : user?.username
      ? formatUserHandle(user.username)
      : "";
  const level = user?.level ?? user?.storage_level;
  const coins = user?.coins ?? user?.coin_balance;
  const diamonds = user?.diamonds ?? user?.diamond_balance;
  const hasPremiumSubscription = !isDeletedUser && Boolean(user?.has_premium_subscription);

  // Avatar priority:
  // 1) user-uploaded avatar (absolute URL from backend)
  // 2) skin-based avatar (name from backend; frontend maps it to a static asset path)
  const avatarSrc =
    isDeletedUser
      ? null
      : avatarSrcOverride || resolveUserAvatarSrc(user);

  // Unread counter (for DM cards in ChatsModal)
  // Takes explicit `unreadCount` prop first, then falls back to user.unread_count if present.
  const unreadRaw = unreadCount ?? user?.unread_count ?? 0;
  const unread = Number(unreadRaw || 0);
  const showUnreadBadge = unread > 0;
  const unreadLabel = unread > 999 ? "999+" : String(unread);
  const showSelectionBorder = selectionMode && isSelectable;
  const isInteractiveCard =
    (selectionMode && isSelectable) || (!isDeletedUser && Boolean(code));
  const roleBorderStyle = roleBorder === "both"
    ? {
        border: "1.5px solid transparent",
        background: "linear-gradient(#102235,#102235) padding-box, linear-gradient(90deg,#70A9FF 0% 50%,#65D99B 50% 100%) border-box",
      }
    : roleBorder === "seeks" || roleBorder === "offers"
      ? {
          borderColor: roleBorder === "seeks" ? "#70A9FF" : "#65D99B",
          borderWidth: "1.5px",
          backgroundColor: "rgba(16,34,53,0.78)",
        }
      : undefined;

  const handleCardActivate = () => {
    if (selectionMode) {
      if (isSelectable) onSelectParticipant?.(user);
      return;
    }
    if (isDeletedUser) return;
    (onOpenProfile || openProfileCard)(user);
  };

  const handleCardKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleCardActivate();
  };

  return (
    <div
      role={isInteractiveCard ? "button" : undefined}
      tabIndex={isInteractiveCard ? 0 : undefined}
      onClick={handleCardActivate}
      onKeyDown={isInteractiveCard ? handleCardKeyDown : undefined}
      className={`relative flex h-[80px] w-full min-w-0 items-center rounded-bl-[67px] rounded-br-[24px] rounded-tl-[67px] rounded-tr-[24px] border border-solid py-[9px] pl-[9px] pr-[10px] text-left transition active:scale-[0.995] ${
        selected
          ? "border-dashed border-[#8BBCFF] bg-white/[0.06] shadow-[0_10px_24px_rgba(0,0,0,0.22)]"
          : showSelectionBorder
          ? "border-dashed border-white/60 bg-transparent"
          : "border-white/50 bg-transparent"
      } ${selectionMode && !isSelectable ? "opacity-45" : ""} ${isDeletedUser && !selectionMode ? "cursor-default" : ""}`}
      style={roleBorderStyle}
      aria-label={isDeletedUser ? DELETED_USER_LABEL : "open-participant"}
      aria-pressed={selectionMode ? selected : undefined}
    >
      {(isHost || badgeLabel) && (
        <div
          className={`pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2.5 z-20 px-3 h-5 rounded-full flex items-center justify-center border border-solid border-white/50 ${
            surfaceControls ? "bg-[#1A2737]" : "bg-[#0D1428]"
          }`}
          aria-hidden="true"
        >
          <span className="text-white text-[11px] font-extrabold leading-none uppercase tracking-wide">
            {badgeLabel || "HOST"}
          </span>
        </div>
      )}
      {showUnreadBadge && (
        <div
          className="pointer-events-none absolute -left-2 -top-2 z-20 bg-white flex items-center justify-center h-6 min-w-6 px-2 rounded-full"
          aria-hidden="true"
        >
          <span className="text-[#002A5D] text-[13px] font-extrabold leading-none">
            {unreadLabel}
          </span>
        </div>
      )}
      {/* Optional legacy kick affordance. Chat participant lists use the shared removal modal instead. */}
      {showKickControl && canKick && !user?.is_self && !isDeletedUser && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onKickClick?.(user); }}
          className={`absolute -right-1.5 -top-2.5 flex h-7 w-7 select-none items-center justify-center rounded-full border p-0 leading-none shadow-[0px_0px_8px_rgba(0,0,0,0.34)] transition active:scale-[0.98] ${
            surfaceControls
              ? "border-[#FF99A7]/55 bg-[#E9485C]/90"
              : "border-white/45 bg-[#E9485C]/90"
          }`}
          aria-label="kick-user"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className="block shrink-0"
          >
            <path
              d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5"
              stroke="white"
              strokeWidth="2.1"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      <div className="flex min-w-0 flex-1 items-center">
        {/* Avatar */}
        <button type="button" disabled={isDeletedUser || (!selectionMode && !code)}
          aria-label={code ? `Open ${code}'s profile` : undefined}
          onClick={(event) => {
            if (selectionMode) return;
            event.stopPropagation();
            (onOpenAvatarProfile || openProfileCard)(user);
          }}
          onKeyDown={(event) => { if (!selectionMode) event.stopPropagation(); }}
          className={`mr-3 flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/70 ${
            isDeletedUser ? "bg-white/12" : "bg-sky-500"
          }`}
        >
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={username || code || ""}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                // If mapped static asset is missing, fall back to empty circle
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </button>

        {isDeletedUser ? (
          <div className="min-w-0 flex-1">
            <div className="truncate text-white text-[16px] font-extrabold leading-5">
              {DELETED_USER_LABEL}
            </div>
          </div>
        ) : (
          <>
            <div className="flex min-w-0 flex-1 flex-col justify-center">
              {hasPremiumSubscription ? (
                <PremiumCodeLabel
                  code={code}
                  isPremium
                  className="max-w-full truncate text-[20px] font-black leading-[22px] text-white"
                  as="div"
                />
              ) : (
                <div className="max-w-full truncate text-[20px] font-black leading-[22px] text-white">
                  {code}
                </div>
              )}
              <div className="mt-[2px] max-w-full truncate text-[15px] font-semibold leading-[17px] text-white">
                {username}
              </div>
            </div>

            <div className="ml-2 flex h-[55px] w-[136px] shrink-0 items-center justify-end overflow-hidden">
              <div className="flex shrink-0 items-center gap-[8px]">
                <div className="grid grid-cols-[58px_18px] items-center gap-x-1 gap-y-[6px]">
                  <span className="text-right text-[15px] font-semibold leading-[17px] text-white">
                    {formatCount(coins)}
                  </span>
                  <img
                    src="/assets/vault/coin_icon.svg"
                    alt=""
                    className="h-[18px] w-[18px] shrink-0 object-contain -translate-y-[1px]"
                  />
                  <span className="text-right text-[15px] font-semibold leading-[17px] text-white">
                    {formatCount(diamonds)}
                  </span>
                  <img
                    src="/assets/vault/diamand_icon.svg"
                    alt=""
                    className="h-[18px] w-[18px] shrink-0 object-contain"
                  />
                </div>
                <div className="flex h-[48px] w-[48px] shrink-0 flex-col items-center justify-center rounded-lg border border-white/24 text-white">
                  <span className="text-center text-[15px] font-bold leading-[17px]">
                    {formatCount(level)}
                  </span>
                  <span className="text-center text-[10px] font-semibold leading-[11px]">
                    level
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { ParticipantCard };

function ParticipantCardSkeleton({ delay = 0 }) {
  return (
    <div
      className="vg-chat-loading-row relative h-[80px] w-full rounded-bl-[67px] rounded-br-[24px] rounded-tl-[67px] rounded-tr-[24px] border border-solid border-white/35 py-[9px] pl-[9px] pr-[10px]"
      style={{ animationDelay: `${delay}ms` }}
      aria-hidden="true"
    >
      <div className="flex h-full items-center gap-2">
        <div className="h-[60px] w-[60px] shrink-0 rounded-full bg-white/12" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="h-5 w-[54px] rounded-md bg-white/18" />
            <div className="flex shrink-0 items-center gap-3">
              <div className="h-5 w-[74px] rounded-full bg-white/14" />
              <div className="h-12 w-12 rounded-lg bg-white/14" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="h-5 w-[112px] rounded-full bg-white/14" />
            <div className="h-5 w-[54px] rounded-full bg-white/14" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ParticipantsLoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: 7 }).map((_, index) => (
        <ParticipantCardSkeleton key={index} delay={40 + index * 80} />
      ))}
    </div>
  );
}

export default function ChatParticipants({
  eventId,
  participantsEndpoint = null,
  canKick = false,
  onParticipantsCountChange,
  onParticipantsLoaded,
  reloadKey = 0,
  selectionMode = false,
  selectedParticipantId = null,
  canSelectParticipant = null,
  onSelectParticipant = null,
}) {
  const navigate = useNavigate();

  const { openProfileCard } = useProfileCardModal();
  const openParticipantVault = useCallback((user) => {
    if (user?.code) openProfileCard(user);
  }, [openProfileCard]);

  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [hostClub, setHostClub] = useState(null);
  const participantsRef = useRef([]);
  const participantsCountChangeRef = useRef(onParticipantsCountChange);
  const participantsLoadedRef = useRef(onParticipantsLoaded);

  useEffect(() => {
    participantsCountChangeRef.current = onParticipantsCountChange;
  }, [onParticipantsCountChange]);

  useEffect(() => {
    participantsLoadedRef.current = onParticipantsLoaded;
  }, [onParticipantsLoaded]);

  useEffect(() => {
    participantsRef.current = participants;
  }, [participants]);

  // If parent doesn't pass canKick=true, we can still infer it from participants payload
  // (when backend marks the current user with is_self and provides their role).
  const [derivedCanKick, setDerivedCanKick] = useState(false);

  const canKickEffective = !!canKick || !!derivedCanKick;
  const getSelectionId = useCallback((participant) => {
    const userId = Number(participant?.user_id || participant?.user?.id || participant?.id || 0);
    if (Number.isFinite(userId) && userId > 0) return `id:${userId}`;
    const code = String(participant?.code || participant?.user?.code || "").trim().toUpperCase();
    return code ? `code:${code}` : "";
  }, []);

  const openClubPage = useCallback(
    (club, fallbackPath) => {
      const path = String(
        club?.public_path || fallbackPath || buildClubPath(club?.id, club?.name)
      ).trim();
      if (!path) return;
      navigate(path);
    },
    [navigate]
  );

  const isUserHost = useCallback(
    (u) => {
      if (!u) return false;
      const r =
        u?.chat_role ||
        u?.role ||
        u?.member_role ||
        u?.event_role ||
        u?.author_role ||
        null;
      return isHostRole(r);
    },
    []
  );

  useEffect(() => {
    const endpoint =
      participantsEndpoint ||
      (eventId ? `/chats/${eventId}/internal-chat/participants/` : null);

    if (!endpoint) return;

    let cancelled = false;

    async function loadParticipants() {
      setLoading((prev) => (participantsRef.current.length ? prev : true));
      try {
        const resp = await api.get(endpoint);
        if (cancelled) return;

        const list = resp?.data?.participants || [];
        setHostClub(resp?.data?.club || null);
        setParticipants(list);

        const resolvedCount = Number(resp?.data?.participants_count);
        const nextCount =
          Number.isFinite(resolvedCount) && resolvedCount >= 0
            ? resolvedCount
            : list.length;

        if (typeof participantsCountChangeRef.current === "function") {
          participantsCountChangeRef.current(nextCount);
        }

        // Try to infer host/admin rights from the current user entry
        const me = list.find((u) => u?.is_self || u?.is_me || u?.me);
        const roleFromMe =
          me?.chat_role ||
          me?.role ||
          me?.member_role ||
          me?.event_role ||
          me?.author_role ||
          null;

        const nextDerivedCanKick = isHostRole(roleFromMe);
        setDerivedCanKick(nextDerivedCanKick);
        if (typeof participantsLoadedRef.current === "function") {
          participantsLoadedRef.current({
            participants: list,
            participantsCount: nextCount,
            club: resp?.data?.club || null,
            canKick: !!canKick || nextDerivedCanKick,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setHostClub(null);
          setParticipants([]);
          setDerivedCanKick(false);
          if (typeof participantsLoadedRef.current === "function") {
            participantsLoadedRef.current({
              participants: [],
              participantsCount: 0,
              club: null,
              canKick: false,
            });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadParticipants();

    return () => {
      cancelled = true;
    };
    // We intentionally do not depend on onParticipantsCountChange here.
    // Parents recreate that callback frequently, and refetching the whole list on each
    // unrelated rerender causes visible jumps in the participants screen.
  }, [eventId, participantsEndpoint, reloadKey, canKick]);

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div
        className="pointer-events-none absolute inset-x-0 top-[-12px] z-10 h-[86px]"
        style={{
          background:
            "linear-gradient(180deg, #0D1428 0%, rgba(13,20,40,0.96) 24%, rgba(13,20,40,0.62) 58%, rgba(13,20,40,0) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-5 pt-12 pb-[calc(var(--bottom-nav-clearance,112px)+16px)] [-webkit-overflow-scrolling:touch] lg:pt-10 lg:pb-6">
        {loading && participants.length === 0 ? <ParticipantsLoadingSkeleton /> : null}
        <div className="flex flex-col gap-3">
          {hostClub ? (
            <ParticipantClubCard club={hostClub} onOpenClub={openClubPage} />
          ) : null}

          {prioritizePremiumChatParticipants(participants)
            .map((u, idx) => (
              <ParticipantCard
                key={u?.id ?? `p-${idx}`}
                user={u}
                canKick={canKickEffective}
                onKickClick={null}
                onOpenProfile={openParticipantVault}
                onSelectParticipant={onSelectParticipant}
                isHost={isUserHost(u)}
                selected={Boolean(selectedParticipantId) && getSelectionId(u) === selectedParticipantId}
                selectionMode={selectionMode}
                isSelectable={
                  typeof canSelectParticipant === "function"
                    ? Boolean(canSelectParticipant(u))
                    : true
                }
                showKickControl={false}
              />
            ))}
        </div>

        {/* optional spacer so last card doesn't glue to bottom nav */}
        <div className="h-2" />
      </div>
    </div>
  );
}
